/* =========================================================================
   Push notifications — Phase 1 (iOS native wrapper only).

   The PWABuilder-generated iOS shell (iOS/Catholics/src) bridges push through
   `window.webkit.messageHandlers.*` + CustomEvents dispatched on `window`
   (see PushNotifications.swift / AppDelegate.swift). This module is the only
   thing on the web side that needs to know that contract exists.

   Android/browser-installed-PWA (standard PushManager/VAPID) is Phase 2 — not
   wired here yet; isIosWrapper() gates every call so this module is a no-op
   everywhere else until then.
   ========================================================================= */

import { postApnsRegistration, deleteNotificationDevice } from './api';

type PermissionResult = 'granted' | 'denied';

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: Record<string, { postMessage: (body: unknown) => void }>;
    };
  }
}

/** The Swift wrapper's custom user agent ends in "...PWAShell" (WebView.swift). */
export function isIosWrapper(): boolean {
  return typeof navigator !== 'undefined' && navigator.userAgent.includes('PWAShell');
}

function postToNative(handler: string, body: unknown = ''): boolean {
  const h = window.webkit?.messageHandlers?.[handler];
  if (!h) return false;
  h.postMessage(body);
  return true;
}

function once<T>(eventName: string, timeoutMs = 8000): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener(eventName, onEvent);
      resolve(null);
    }, timeoutMs);
    function onEvent(e: Event) {
      clearTimeout(timer);
      window.removeEventListener(eventName, onEvent);
      resolve((e as CustomEvent<T>).detail);
    }
    window.addEventListener(eventName, onEvent);
  });
}

/** Triggers the native permission dialog (only fires the system prompt once per
    install — call this from a deliberate user gesture, e.g. tapping "Enable
    Notifications" in the preferences panel, never on load). */
export async function requestPermission(): Promise<PermissionResult | null> {
  if (!isIosWrapper()) return null;
  const pending = once<PermissionResult>('push-permission-request');
  if (!postToNative('push-permission-request')) return null;
  return pending;
}

export async function getPermissionState(): Promise<string | null> {
  if (!isIosWrapper()) return null;
  const pending = once<string>('push-permission-state');
  if (!postToNative('push-permission-state')) return null;
  return pending;
}

/** Resolves with the raw APNs hex device token, or null if not authorized/unavailable. */
export async function getApnsToken(): Promise<string | null> {
  if (!isIosWrapper()) return null;
  const pending = once<string>('push-token');
  if (!postToNative('push-token')) return null;
  const token = await pending;
  return token && token !== 'ERROR GET TOKEN' && token !== 'NOT_AUTHORIZED' ? token : null;
}

/** Full opt-in flow: permission → token → register with the API. Returns true on success. */
export async function enablePush(topics: string[]): Promise<boolean> {
  if (!isIosWrapper()) return false;
  const permission = await requestPermission();
  if (permission !== 'granted') return false;
  const token = await getApnsToken();
  if (!token) return false;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const result = await postApnsRegistration(token, topics, tz);
  return result.ok;
}

export async function disablePush(): Promise<boolean> {
  if (!isIosWrapper()) return false;
  const token = await getApnsToken();
  if (!token) return false;
  const result = await deleteNotificationDevice(token);
  return result.ok;
}

export function onNotification(cb: (payload: unknown) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent).detail);
  window.addEventListener('push-notification', handler);
  return () => window.removeEventListener('push-notification', handler);
}

export function onNotificationClick(cb: (payload: unknown) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent).detail);
  window.addEventListener('push-notification-click', handler);
  return () => window.removeEventListener('push-notification-click', handler);
}
